import { useState, type CSSProperties } from 'react';
import {
  resolveProtectivePrices,
  type ProtectiveState,
} from './ProtectiveOrders';

export type OpenPosition = {
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  unrealizedPnl?: number;
  markPrice?: number;
  leverage?: number;
};

type Labels = {
  title?: string;
  closePct?: string;
  closeBtn?: string;
  closeHint?: string;
  attachProtective?: string;
  protectiveHint?: string;
  dismiss?: string;
  busy?: string;
  size?: string;
  entry?: string;
  upnl?: string;
  qtyClose?: string;
  moreProtective?: string;
  lessProtective?: string;
};

type Props = {
  position: OpenPosition;
  closePct: number;
  onClosePct: (pct: number) => void;
  protective: ProtectiveState;
  onProtective: (next: ProtectiveState) => void;
  busy?: boolean;
  onClose: () => void;
  onAttachProtective: () => void;
  onDismiss?: () => void;
  labels?: Labels;
  /** Compact = chart floating card */
  compact?: boolean;
};

const fmt = (n: unknown, d = 4) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  const abs = Math.abs(x);
  const digits = abs > 0 && abs < 0.01 ? Math.max(d, 6) : abs < 1 ? Math.max(d, 5) : d;
  return x.toLocaleString('en-US', { maximumFractionDigits: digits });
};

const PRESETS = [25, 50, 75, 100] as const;

export default function PositionTools({
  position, closePct, onClosePct, protective, onProtective,
  busy, onClose, onAttachProtective, onDismiss, labels = {}, compact = false,
}: Props) {
  const [showProtect, setShowProtect] = useState(false);
  const L = {
    closeBtn: labels.closeBtn || 'Close',
    attachProtective: labels.attachProtective || 'Set',
    dismiss: labels.dismiss || 'Close panel',
    busy: labels.busy || '…',
    moreProtective: labels.moreProtective || 'TP/SL',
    lessProtective: labels.lessProtective || 'Hide',
  };

  const pct = Math.max(1, Math.min(100, Math.round(closePct)));
  const closeQty = position.size * (pct / 100);
  const long = position.side === 'long';
  const orderSide = long ? 'buy' : 'sell';
  const upnl = Number(position.unrealizedPnl);
  const px = Number(position.markPrice);
  const notionalPx = Number.isFinite(px) && px > 0 ? px : position.entryPrice;
  const notionalUsdt = position.size * notionalPx;
  const closeUsdt = closeQty * notionalPx;
  const estClosePnl = Number.isFinite(upnl) ? upnl * (pct / 100) : null;
  const color = long ? 'var(--long)' : 'var(--short)';
  const resolved = resolveProtectivePrices(position.entryPrice, orderSide, protective);
  const set = (patch: Partial<ProtectiveState>) => onProtective({ ...protective, ...patch });

  const tpOn = protective.tpMode !== 'off';
  const slOn = protective.slMode !== 'off';
  const trailOn = protective.trailMode === 'on';

  return (
    <div style={{
      marginTop: compact ? 0 : 4,
      marginLeft: compact ? 0 : 'auto',
      maxWidth: compact ? '100%' : 320,
      width: compact ? '100%' : 'min(100%, 320px)',
      padding: compact ? '5px 7px' : '3px 5px',
      borderRadius: 5,
      border: `1px solid ${long ? 'rgba(61,186,122,0.45)' : 'rgba(226,91,91,0.45)'}`,
      background: long ? 'rgba(61,186,122,0.05)' : 'rgba(226,91,91,0.05)',
      boxSizing: 'border-box',
    }}>
      {/* Identity — one dense line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap',
        fontFamily: 'var(--mono)', fontSize: 10, lineHeight: 1.2, overflow: 'hidden',
      }}>
        <span style={{ fontWeight: 700, color }}>{long ? 'L' : 'S'}</span>
        <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {position.symbol}
        </span>
        {position.leverage ? <span style={{ color: 'var(--muted)' }}>{position.leverage}x</span> : null}
        <span style={{ color: 'var(--muted)' }} title={`${fmt(position.size, 4)} coin`}>
          {fmt(notionalUsdt, 2)} U
        </span>
        <span style={{ color: 'var(--muted)' }}>@{fmt(position.entryPrice)}</span>
        <span style={{ color: Number.isFinite(upnl) && upnl >= 0 ? 'var(--long)' : 'var(--short)' }}>
          {Number.isFinite(upnl) ? `${upnl >= 0 ? '+' : ''}${fmt(upnl, 2)}` : '—'}
        </span>
        <span style={{ flex: 1, minWidth: 4 }} />
        {onDismiss ? (
          <button type="button" onClick={onDismiss} style={iconBtn} title={L.dismiss}>✕</button>
        ) : null}
      </div>

      {/* Close controls — tight */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 3, marginTop: 4, flexWrap: 'nowrap',
      }}>
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            disabled={busy}
            onClick={() => onClosePct(p)}
            style={{
              ...miniChip,
              borderColor: pct === p ? 'var(--gold)' : 'var(--line)',
              color: pct === p ? 'var(--gold)' : 'var(--muted)',
              padding: '2px 4px',
              minWidth: 28,
            }}
          >
            {p}
          </button>
        ))}
        <input
          type="range"
          min={1}
          max={100}
          step={1}
          value={pct}
          disabled={busy}
          onChange={(e) => onClosePct(Number(e.target.value))}
          style={{ flex: 1, minWidth: 44, maxWidth: 72, height: 10, accentColor: '#c9a227' }}
          title={`${pct}% · ${fmt(closeUsdt, 2)} USDT`}
        />
        <span style={{
          fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--gold)', minWidth: 26, textAlign: 'right',
        }}>
          {pct}%
        </span>
        <button
          type="button"
          disabled={busy || !(closeQty > 0)}
          onClick={onClose}
          title={estClosePnl != null
            ? `~${estClosePnl >= 0 ? '+' : ''}${fmt(estClosePnl, 2)} USDT · ${fmt(closeUsdt, 2)} U`
            : `${fmt(closeUsdt, 2)} USDT`}
          style={{ ...closeBtn, opacity: busy ? 0.7 : 1 }}
        >
          {busy ? L.busy : L.closeBtn}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setShowProtect((v) => !v)}
          style={{
            ...miniChip,
            borderColor: showProtect || tpOn || slOn || trailOn ? 'var(--gold)' : 'var(--line)',
            color: showProtect || tpOn || slOn || trailOn ? 'var(--gold)' : 'var(--muted)',
            minWidth: 40,
            padding: '2px 5px',
          }}
        >
          {showProtect ? L.lessProtective : L.moreProtective}
        </button>
      </div>

      {showProtect ? (
        <div style={{
          marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--line)',
          display: 'grid', gap: 3,
        }}>
          <div style={protRow}>
            <button
              type="button"
              onClick={() => set({ tpMode: tpOn ? 'off' : 'percent' })}
              style={{
                ...miniChip,
                borderColor: tpOn ? 'var(--long)' : 'var(--line)',
                color: tpOn ? 'var(--long)' : 'var(--muted)',
                minWidth: 28,
              }}
            >
              TP
            </button>
            <select
              value={protective.tpMode === 'off' ? 'percent' : protective.tpMode}
              onChange={(e) => set({ tpMode: e.target.value as ProtectiveState['tpMode'] })}
              style={{ ...sel, opacity: tpOn ? 1 : 0.45 }}
              disabled={!tpOn}
            >
              <option value="percent">%</option>
              <option value="price">px</option>
            </select>
            <input
              value={protective.tpValue}
              disabled={!tpOn}
              onChange={(e) => set({
                tpMode: tpOn ? protective.tpMode : 'percent',
                tpValue: e.target.value,
              })}
              style={{ ...inp, opacity: tpOn ? 1 : 0.45 }}
              placeholder={protective.tpMode === 'price' ? 'price' : '1.5'}
            />
            <span style={hint}>{resolved.tp != null ? fmt(resolved.tp) : '—'}</span>
          </div>

          <div style={protRow}>
            <button
              type="button"
              onClick={() => set({ slMode: slOn ? 'off' : 'percent' })}
              style={{
                ...miniChip,
                borderColor: slOn ? 'var(--short)' : 'var(--line)',
                color: slOn ? 'var(--short)' : 'var(--muted)',
                minWidth: 28,
              }}
            >
              SL
            </button>
            <select
              value={protective.slMode === 'off' ? 'percent' : protective.slMode}
              onChange={(e) => set({ slMode: e.target.value as ProtectiveState['slMode'] })}
              style={{ ...sel, opacity: slOn ? 1 : 0.45 }}
              disabled={!slOn}
            >
              <option value="percent">%</option>
              <option value="price">px</option>
            </select>
            <input
              value={protective.slValue}
              disabled={!slOn}
              onChange={(e) => set({
                slMode: slOn ? protective.slMode : 'percent',
                slValue: e.target.value,
              })}
              style={{ ...inp, opacity: slOn ? 1 : 0.45 }}
              placeholder={protective.slMode === 'price' ? 'price' : '1'}
            />
            <span style={hint}>{resolved.sl != null ? fmt(resolved.sl) : '—'}</span>
          </div>

          <div style={{ ...protRow, gridTemplateColumns: 'auto 1fr 1fr auto' }}>
            <button
              type="button"
              onClick={() => set({ trailMode: trailOn ? 'off' : 'on' })}
              style={{
                ...miniChip,
                borderColor: trailOn ? 'var(--gold)' : 'var(--line)',
                color: trailOn ? 'var(--gold)' : 'var(--muted)',
                minWidth: 28,
              }}
            >
              TR
            </button>
            <input
              value={protective.trailCallbackPct}
              disabled={!trailOn}
              onChange={(e) => set({ trailMode: 'on', trailCallbackPct: e.target.value })}
              style={{ ...inp, opacity: trailOn ? 1 : 0.45 }}
              placeholder="cb %"
              title="Trailing callback %"
            />
            <input
              value={protective.trailActivate}
              disabled={!trailOn}
              onChange={(e) => set({ trailMode: 'on', trailActivate: e.target.value })}
              style={{ ...inp, opacity: trailOn ? 1 : 0.45 }}
              placeholder="activate"
              title="Activate price (optional)"
            />
            <span style={hint}>{trailOn ? 'trail' : '—'}</span>
          </div>

          <button
            type="button"
            disabled={busy || (!tpOn && !slOn && !trailOn)}
            onClick={onAttachProtective}
            style={{
              ...setBtn,
              opacity: busy || (!tpOn && !slOn && !trailOn) ? 0.55 : 1,
            }}
          >
            {busy ? L.busy : L.attachProtective}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const miniChip: CSSProperties = {
  padding: '2px 5px', borderRadius: 4, border: '1px solid var(--line)',
  background: 'var(--panel)', cursor: 'pointer', fontSize: 10, fontFamily: 'var(--mono)',
};
const iconBtn: CSSProperties = {
  padding: '1px 4px', borderRadius: 4, border: '1px solid transparent',
  background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, lineHeight: 1,
};
const closeBtn: CSSProperties = {
  padding: '2px 7px', borderRadius: 4,
  border: '1px solid var(--short)', background: 'rgba(226,91,91,0.16)',
  color: 'var(--short)', fontWeight: 700, cursor: 'pointer', fontSize: 10,
};
const setBtn: CSSProperties = {
  padding: '4px 8px', borderRadius: 4, border: 'none',
  background: 'linear-gradient(180deg,#dfb83a,var(--gold))',
  color: '#111', fontWeight: 700, cursor: 'pointer', fontSize: 10,
};
const protRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 44px 1fr auto',
  gap: 3,
  alignItems: 'center',
};
const sel: CSSProperties = {
  padding: '2px 3px', borderRadius: 4, border: '1px solid var(--line)',
  background: 'var(--panel)', color: 'var(--text)', fontSize: 10, fontFamily: 'var(--mono)',
};
const inp: CSSProperties = {
  width: '100%', padding: '3px 5px', borderRadius: 4,
  border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--text)',
  fontSize: 10, fontFamily: 'var(--mono)',
};
const hint: CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', minWidth: 40, textAlign: 'right',
};

/** Stable id for a position row / chart hit. */
export const positionKey = (p: { symbol?: string; side?: string }) =>
  `${String(p.symbol || '').toUpperCase()}:${String(p.side || '').toLowerCase()}`;

export const normalizeOpenPosition = (p: any): OpenPosition | null => {
  const symbol = String(p?.symbol || '').toUpperCase().replace(/^CMT_?/i, '');
  const size = Math.abs(Number(p?.size ?? p?.positionAmt ?? 0));
  const entry = Number(p?.entryPrice);
  if (!symbol || !(size > 0) || !Number.isFinite(entry) || entry <= 0) return null;
  const sideRaw = String(p?.side || '').toLowerCase();
  const amt = Number(p?.positionAmt ?? 0);
  const side: 'long' | 'short' = sideRaw.includes('short') || amt < 0 ? 'short' : 'long';
  return {
    symbol,
    side,
    size,
    entryPrice: entry,
    unrealizedPnl: Number(p?.unrealizedPnl),
    markPrice: Number.isFinite(Number(p?.markPrice)) ? Number(p.markPrice) : undefined,
    leverage: Number.isFinite(Number(p?.leverage)) ? Number(p.leverage) : undefined,
  };
};
